import { msg } from 'gt-react';
import { PackageIcon } from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import { ProductModule } from '@erp/shared-types';
import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { z } from 'zod';
import {
  Route as pathlessAuthenticatedRoute,
  requireModules,
  requirePermission,
} from '../route';

const ProductCatalogPage = lazyRouteComponent(
  () => import('./ProductCatalogPage'),
);

export const Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/product-catalog',
  staticData: {
    app: {
      nav: {
        label: msg('Product Catalog', {
          context: 'sidebar nav label, Sales section',
        }),
        icon: <PackageIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 26,
      },
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        // Gated by the Read:ProductCatalog RBAC permission (Sales › Orders),
        // independent of Product Margins so pricing visibility is grantable
        // on its own.
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.ProductCatalog },
        ],
      },
    },
  },
  component: ProductCatalogPage,
  loader: () => ({
    crumb: msg('Product Catalog', { context: 'page breadcrumb' }),
  }),
  // Pagination + filters live in the URL so a refresh / shared link restores
  // the full view. Each field `.catch(undefined)` so a malformed param can't
  // crash the page.
  validateSearch: z.object({
    page: z.number().int().min(1).optional().catch(undefined),
    pageSize: z.number().int().min(1).max(200).optional().catch(undefined),
    search: z.string().optional().catch(undefined),
    family: z.string().optional().catch(undefined),
    group: z.string().optional().catch(undefined),
    model: z.string().optional().catch(undefined),
    // Multi-selects are stored comma-separated for clean URLs (vs JSON arrays,
    // which serialize as %5B%22..%22%5D). The page splits/joins at the boundary.
    customerId: z.string().optional().catch(undefined),
    priceListId: z.string().optional().catch(undefined),
    noPriceList: z.boolean().optional().catch(undefined),
    status: z.enum(['active', 'inactive', 'all']).optional().catch(undefined),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.ORDER_MANAGEMENT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ProductCatalog,
    });
  },
});
